import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = 'qf_token';
const USER_KEY = 'qf_user';

export function getToken() {
    return localStorage.getItem(TOKEN_KEY);
}
export function setToken(t) {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
}
export function getStoredUser() {
    try {
        const u = localStorage.getItem(USER_KEY);
        return u ? JSON.parse(u) : null;
    } catch (_e) {
        return null;
    }
}
export function setStoredUser(u) {
    if (u) localStorage.setItem(USER_KEY, JSON.stringify(u));
    else localStorage.removeItem(USER_KEY);
}

const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
    const token = getToken();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

api.interceptors.response.use(
    (resp) => resp,
    (error) => {
        if (error?.response?.status === 401) {
            // Don't auto-logout if it's just login/signup failing
            const url = error.config?.url || '';
            if (!url.includes('/auth/login') && !url.includes('/auth/signup')) {
                // Surface the friendlier "signed in elsewhere" message when the
                // backend reports a token-version mismatch (single-session
                // enforcement on Solo). For all other 401s, just bounce silently
                // to /login as before.
                const detail = error.response?.data?.detail || '';
                const isElsewhere = /signed in elsewhere/i.test(detail);
                if (isElsewhere && typeof window !== 'undefined') {
                    // Hand off to a top-level listener that owns the toast UX so
                    // we don't import sonner in this low-level module.
                    window.dispatchEvent(new CustomEvent('qf:signed-in-elsewhere', { detail: { detail } }));
                }
                setToken(null);
                setStoredUser(null);
                if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
                    const next = isElsewhere ? '/login?reason=signed-in-elsewhere' : '/login';
                    window.location.href = next;
                }
            }
        }
        // 402 = no active/trialing subscription OR insufficient credits.
        // Fire the global paywall modal so the user gets a clear path to
        // start their 7-day free trial (or top up if Solo).
        if (error?.response?.status === 402 && typeof window !== 'undefined') {
            const detail = error.response.data?.detail
                || 'Start your 7-day free trial to keep using QuotesFlow.';
            window.dispatchEvent(new CustomEvent('qf:paywall', { detail: { detail } }));
        }
        return Promise.reject(error);
    }
);

export default api;

export function logout() {
    // ── Centralized scorched-earth cleanup of per-user storage ──
    // Anything account-scoped MUST be cleared so the next user signing in
    // doesn't see the previous user's chat history, search filters, etc.
    // Device-level keys (cookie consent, "I dismissed the nudge") stay.
    const PER_USER_SESSION_KEYS = [
        'qf-support-chat-history',   // Support chatbot conversation
        'qf-leadfinder-state',       // Lead Finder filters + cached results
    ];
    PER_USER_SESSION_KEYS.forEach(k => {
        try { sessionStorage.removeItem(k); } catch { /* noop */ }
    });
    // Defensive sweep — any other `qf-*` session keys we forgot to enumerate.
    try {
        for (let i = sessionStorage.length - 1; i >= 0; i--) {
            const key = sessionStorage.key(i);
            if (key && key.startsWith('qf-')) sessionStorage.removeItem(key);
        }
    } catch { /* noop */ }

    setToken(null);
    setStoredUser(null);
    window.location.href = '/';
}
